import type { ApiFieldDef, FieldDataType, FieldValidation } from '@/types/inventory'

// ─── Client-side mirror of the backend field validator ───────────────────────
//
// Mirrors `validateFieldValues` / `applyFieldRules` in
// `cgt-backend/src/inventory/profiles/profiles.support.ts` +
// `.../field-defs/field-validation.ts`. The server stays the authority — this
// exists so a bad value is caught inline while the user is typing instead of
// coming back as an opaque 400 after they hit Save.
//
// Pure: no React, no network. Keep it that way so it stays unit-testable.

/** Which rule keys apply to which dataType. `required` applies to all. */
export const RULES_BY_TYPE: Record<FieldDataType, readonly (keyof FieldValidation)[]> = {
  string: ['minLength', 'maxLength', 'pattern'],
  number: ['min', 'max', 'integer', 'step'],
  boolean: [],
  date: ['minDate', 'maxDate'],
  enum: ['multiple'],
  file: ['accept', 'maxSizeMb'],
}

export const DATA_TYPE_LABELS: Record<FieldDataType, string> = {
  string: 'Text',
  number: 'Number',
  boolean: 'Yes / No',
  date: 'Date',
  enum: 'Select',
  file: 'File',
}

function isBlank(v: unknown): boolean {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '')
}

/**
 * Convert a raw input value into the shape the backend expects for `dataType`.
 *
 * The reason this exists: an `<input type='number'>` still hands back a STRING,
 * and the backend requires `typeof value === 'number'` — posting the raw value
 * made every number save fail with `Field X must be a number`.
 */
export function coerceFieldValue(dataType: FieldDataType, raw: unknown): unknown {
  if (isBlank(raw)) return null
  if (dataType === 'number') {
    const n = typeof raw === 'number' ? raw : Number(String(raw).trim())
    return Number.isFinite(n) ? n : raw // leave it alone so validation can report it
  }
  if (dataType === 'boolean') return Boolean(raw)
  return raw
}

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot === -1 ? '' : name.slice(dot).toLowerCase()
}

/**
 * The parts of a field definition these validators actually read.
 *
 * Structural, not `ApiFieldDef`, so a definition that is not an INVENTORY one
 * can reuse them — the process-step attribute defs are the same shape minus
 * `profileType`. `ApiFieldDef` satisfies it, so every existing caller is
 * unaffected.
 */
export interface ValidatableFieldDef {
  label: string
  dataType: FieldDataType
  options?: string[]
  validation?: FieldValidation | null
}

/**
 * Validate one already-coerced value against its definition.
 * Returns a human-readable message, or null when the value is acceptable.
 */
export function validateFieldValue(def: ValidatableFieldDef, value: unknown): string | null {
  const rules: FieldValidation = def.validation ?? {}

  if (rules.required && isBlank(value)) return `${def.label} is required`
  if (isBlank(value)) return null // optional + empty is fine; nothing more to check

  switch (def.dataType) {
    case 'number': {
      if (typeof value !== 'number' || Number.isNaN(value)) return `${def.label} must be a number`
      if (rules.min !== undefined && value < rules.min) return `${def.label} must be at least ${rules.min}`
      if (rules.max !== undefined && value > rules.max) return `${def.label} must be at most ${rules.max}`
      if (rules.integer && !Number.isInteger(value)) return `${def.label} must be a whole number`
      return null
    }
    case 'string': {
      const s = String(value)
      if (rules.minLength !== undefined && s.length < rules.minLength) {
        return `${def.label} must be at least ${rules.minLength} characters`
      }
      if (rules.maxLength !== undefined && s.length > rules.maxLength) {
        return `${def.label} must be at most ${rules.maxLength} characters`
      }
      if (rules.pattern) {
        let re: RegExp
        try {
          re = new RegExp(rules.pattern)
        } catch {
          return null // an uncompilable pattern is a config bug; don't block data entry on it
        }
        if (!re.test(s)) return `${def.label} does not match the required format`
      }
      return null
    }
    case 'date': {
      const parsed = Date.parse(String(value))
      if (Number.isNaN(parsed)) return `${def.label} must be a date`
      if (rules.minDate !== undefined && parsed < Date.parse(rules.minDate)) {
        return `${def.label} must be on or after ${rules.minDate}`
      }
      if (rules.maxDate !== undefined && parsed > Date.parse(rules.maxDate)) {
        return `${def.label} must be on or before ${rules.maxDate}`
      }
      return null
    }
    case 'enum': {
      const options = def.options ?? []
      // No options configured = an incomplete def, not invalid data.
      if (options.length === 0) return null
      const picked = Array.isArray(value) ? value : [value]
      if (!rules.multiple && Array.isArray(value)) return `${def.label} accepts a single value`
      for (const v of picked) {
        if (!options.includes(String(v))) return `${def.label} must be one of: ${options.join(', ')}`
      }
      return null
    }
    case 'file': {
      if (!rules.accept?.length) return null
      const name = String(value)
      const ext = extensionOf(name)
      const ok = rules.accept.some((a) =>
        a.startsWith('.') ? a.toLowerCase() === ext : name.toLowerCase().includes(a.toLowerCase()),
      )
      return ok ? null : `${def.label} must be one of these file types: ${rules.accept.join(', ')}`
    }
    default:
      return null
  }
}

/**
 * Validate a whole set of values keyed by field key.
 * Returns `{ [key]: message }` for the fields that failed — empty when all pass.
 */
export function validateFieldValues(
  defs: ApiFieldDef[],
  values: Record<string, unknown>,
): Record<string, string> {
  const errors: Record<string, string> = {}
  for (const def of defs) {
    if (!(def.key in values)) continue
    const message = validateFieldValue(def, coerceFieldValue(def.dataType, values[def.key]))
    if (message) errors[def.key] = message
  }
  return errors
}

/** The `accept` attribute for a file input, from the def's configured list. */
export function acceptAttr(def: ValidatableFieldDef): string | undefined {
  const accept = def.validation?.accept
  return accept?.length ? accept.join(',') : undefined
}

/** '.pdf' → 'PDF', 'application/pdf' → 'application/pdf'. */
function prettyAccept(entry: string): string {
  return entry.startsWith('.') ? entry.slice(1).toUpperCase() : entry
}

/**
 * Human-readable summary of a def's rules, for display next to its input —
 * "PDF or PNG · max 5 MB", "0–100, whole numbers", "max 40 characters".
 *
 * Rules are useless to the person entering data if they only surface as an
 * error after a rejected save; this tells them the constraint up front.
 * Returns null when the field is unconstrained (nothing worth saying).
 */
export function describeFieldRules(def: ValidatableFieldDef): string | null {
  const r = def.validation ?? {}
  const parts: string[] = []

  switch (def.dataType) {
    case 'number': {
      if (r.min !== undefined && r.max !== undefined) parts.push(`${r.min}–${r.max}`)
      else if (r.min !== undefined) parts.push(`min ${r.min}`)
      else if (r.max !== undefined) parts.push(`max ${r.max}`)
      if (r.integer) parts.push('whole numbers')
      break
    }
    case 'string': {
      if (r.minLength !== undefined && r.maxLength !== undefined) {
        parts.push(`${r.minLength}–${r.maxLength} characters`)
      } else if (r.minLength !== undefined) parts.push(`min ${r.minLength} characters`)
      else if (r.maxLength !== undefined) parts.push(`max ${r.maxLength} characters`)
      if (r.pattern) parts.push(`format ${r.pattern}`)
      break
    }
    case 'date': {
      if (r.minDate && r.maxDate) parts.push(`${r.minDate.slice(0, 10)} to ${r.maxDate.slice(0, 10)}`)
      else if (r.minDate) parts.push(`on or after ${r.minDate.slice(0, 10)}`)
      else if (r.maxDate) parts.push(`on or before ${r.maxDate.slice(0, 10)}`)
      break
    }
    case 'enum': {
      const options = def.options ?? []
      if (options.length > 0) parts.push(r.multiple ? 'choose one or more' : 'choose one')
      break
    }
    case 'file': {
      if (r.accept?.length) {
        const names = r.accept.map(prettyAccept)
        parts.push(
          names.length === 1
            ? `${names[0]} only`
            : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`,
        )
      }
      if (r.maxSizeMb !== undefined) parts.push(`max ${r.maxSizeMb} MB`)
      break
    }
    default:
      break
  }

  if (r.required) parts.push('required')
  return parts.length > 0 ? parts.join(' · ') : null
}
